import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase"; // adjust path to match your firebase.js export

const COUPONS_COLLECTION = "coupons";

// A coupon's scope decides which line item its % discount applies to.
// "global" applies the % across golf + ferry + transport + accommodation combined.
export const COUPON_SCOPES = [
  { value: "global", label: "Global (entire booking)" },
  { value: "gateway", label: "Golf Rounds / Package" },
  { value: "ferry", label: "Ferry" },
  { value: "transport", label: "Land Transport" },
  { value: "accommodation", label: "Accommodation" },
];

function normalizeCode(code) {
  return (code || "").trim().toUpperCase();
}

export async function createCoupon(payload) {
  const ref = await addDoc(collection(db, COUPONS_COLLECTION), {
    ...payload,
    code: normalizeCode(payload.code),
    timesUsed: 0,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCoupon(id, payload) {
  await updateDoc(doc(db, COUPONS_COLLECTION, id), {
    ...payload,
    code: normalizeCode(payload.code),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCoupon(id) {
  await deleteDoc(doc(db, COUPONS_COLLECTION, id));
}

export async function getAllCoupons() {
  const snap = await getDocs(collection(db, COUPONS_COLLECTION));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Case-insensitive lookup. Returns null if no coupon with that code exists —
// caller (validateCoupon) decides how to message not-found vs. expired vs. disabled.
export async function getCouponByCode(rawCode) {
  const code = normalizeCode(rawCode);
  if (!code) return null;
  const q = query(
    collection(db, COUPONS_COLLECTION),
    where("code", "==", code),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

// Pure validation — no network calls — so it can run both client-side (for
// instant feedback) and be mirrored server-side later if you add a Cloud
// Function to re-verify at payment time.
export function validateCoupon(coupon) {
  if (!coupon) return "Invalid coupon code.";
  if (coupon.active === false) return "This coupon is no longer active.";
  if (coupon.expiresAt) {
    const exp = new Date(coupon.expiresAt);
    if (!Number.isNaN(exp.getTime()) && exp < new Date()) {
      return "This coupon has expired.";
    }
  }
  if (
    coupon.usageLimit != null &&
    Number(coupon.timesUsed || 0) >= Number(coupon.usageLimit)
  ) {
    return "This coupon has reached its usage limit.";
  }
  return null; // valid
}